import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Pencil, FileDown, X, Check } from 'lucide-react';
import jsPDF from 'jspdf';
import authFetch from '../utils/authFetch';
import './TimeTrackingOverviewPage.css';

const monthLabelFormatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  return monthLabelFormatter.format(new Date(year, month - 1, 1));
};

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const todayIso = () => new Date().toISOString().split('T')[0];
const formatEuropeanDate = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const isoDateMatch = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return `${Number(isoDateMatch[3])}.${Number(isoDateMatch[2])}.${isoDateMatch[1]}`;
  }
  const euroDateMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (euroDateMatch) {
    return `${Number(euroDateMatch[1])}.${Number(euroDateMatch[2])}.${euroDateMatch[3]}`;
  }
  return raw;
};

const TimeTrackingOverviewPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState([]);
  const [totals, setTotals] = useState({ totalHours: 0, totalMinutes: 0, totalEarningsEur: 0 });
  const [employees, setEmployees] = useState([]);
  const [activeRatesByUser, setActiveRatesByUser] = useState({});
  const [selectedRateUserId, setSelectedRateUserId] = useState('');
  const [ratesHistory, setRatesHistory] = useState([]);
  const [filters, setFilters] = useState({ employee: '', dateFrom: '', dateTo: '' });
  const [rateForm, setRateForm] = useState({ hourlyRateEur: '', effectiveFrom: todayIso() });
  const [loading, setLoading] = useState(true);
  const [savingRate, setSavingRate] = useState(false);
  const [error, setError] = useState('');
  const [rateMessage, setRateMessage] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editForm, setEditForm] = useState({ workDate: '', startTime: '', endTime: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedExportMonths, setSelectedExportMonths] = useState([]);
  const [editingRateId, setEditingRateId] = useState(null);
  const [rateEditForm, setRateEditForm] = useState({ hourlyRateEur: '', effectiveFrom: '' });
  const [savingRateEdit, setSavingRateEdit] = useState(false);

  const availableMonths = useMemo(() => {
    const months = new Set();
    entries.forEach((entry) => {
      const key = String(entry.workDate || '').slice(0, 7);
      if (key) months.add(key);
    });
    return Array.from(months).sort().reverse();
  }, [entries]);

  const loadRates = async (userId) => {
    if (!userId) {
      setRatesHistory([]);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/rates?userId=${encodeURIComponent(userId)}`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load rate history');
      }

      setRatesHistory(Array.isArray(payload.rates) ? payload.rates : []);
    } catch (err) {
      setRateMessage(err.message || 'Failed to load rate history');
    }
  };

  const loadOverview = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '1000');
      if (filters.employee) params.set('employee', filters.employee);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);

      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/all?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load all entries');
      }

      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setEmployeeSummary(Array.isArray(payload.employeeSummary) ? payload.employeeSummary : []);
      setTotals(payload.totals || { totalHours: 0, totalMinutes: 0, totalEarningsEur: 0 });
      const loadedEmployees = Array.isArray(payload.employees) ? payload.employees : [];
      setEmployees(loadedEmployees);

      const ratesByUser = {};
      if (Array.isArray(payload.activeRates)) {
        payload.activeRates.forEach((rate) => {
          if (rate && rate.userId) {
            ratesByUser[String(rate.userId)] = rate;
          }
        });
      }
      setActiveRatesByUser(ratesByUser);

      if (!selectedRateUserId && loadedEmployees.length > 0) {
        setSelectedRateUserId(String(loadedEmployees[0].userId));
      }
    } catch (err) {
      setError(err.message || 'Failed to load all entries');
      setEntries([]);
      setEmployeeSummary([]);
      setTotals({ totalHours: 0, totalMinutes: 0, totalEarningsEur: 0 });
      setEmployees([]);
      setActiveRatesByUser({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [filters.employee, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    loadRates(selectedRateUserId);
  }, [selectedRateUserId]);

  const handleSaveRate = async (event) => {
    event.preventDefault();
    setSavingRate(true);
    setRateMessage('');
    setError('');

    try {
      const selectedUser = employees.find((employee) => String(employee.userId) === String(selectedRateUserId));
      if (!selectedUser) {
        throw new Error('Please select a user for this hourly rate.');
      }

      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selectedUser.userId,
          employeeName: selectedUser.employeeName,
          hourlyRateEur: Number(rateForm.hourlyRateEur),
          effectiveFrom: rateForm.effectiveFrom
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to save hourly rate');
      }

      setRateMessage(payload.message || 'Hourly rate saved.');
  await loadRates(selectedRateUserId);
      await loadOverview();
    } catch (err) {
      setRateMessage(err.message || 'Failed to save hourly rate');
    } finally {
      setSavingRate(false);
    }
  };

  const startEditEntry = (entry) => {
    setError('');
    setEditingEntryId(entry.id);
    setEditForm({
      workDate: entry.workDate || '',
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      notes: entry.notes || ''
    });
  };

  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEditForm({ workDate: '', startTime: '', endTime: '', notes: '' });
  };

  const saveEditEntry = async (entryId) => {
    setSavingEdit(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/entries/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update time entry');
      }

      cancelEditEntry();
      await loadOverview();
    } catch (err) {
      setError(err.message || 'Failed to update time entry');
    } finally {
      setSavingEdit(false);
    }
  };

  const startEditRate = (rate) => {
    setRateMessage('');
    setEditingRateId(rate.id);
    setRateEditForm({ hourlyRateEur: String(rate.hourlyRateEur ?? ''), effectiveFrom: rate.effectiveFrom || '' });
  };

  const cancelEditRate = () => {
    setEditingRateId(null);
    setRateEditForm({ hourlyRateEur: '', effectiveFrom: '' });
  };

  const saveEditRate = async (rateId) => {
    setSavingRateEdit(true);
    setRateMessage('');
    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/rates/${rateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hourlyRateEur: Number(rateEditForm.hourlyRateEur),
          effectiveFrom: rateEditForm.effectiveFrom
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update hourly rate');
      }

      setRateMessage(payload.message || 'Hourly rate updated.');
      cancelEditRate();
      await loadRates(selectedRateUserId);
      await loadOverview();
    } catch (err) {
      setRateMessage(err.message || 'Failed to update hourly rate');
    } finally {
      setSavingRateEdit(false);
    }
  };

  const toggleExportMonth = (monthKey) => {
    setSelectedExportMonths((prev) => (
      prev.includes(monthKey) ? prev.filter((key) => key !== monthKey) : [...prev, monthKey]
    ));
  };

  const handleExportPdf = () => {
    if (entries.length === 0) {
      setError('No entries to export.');
      return;
    }

    const grouped = {};
    entries.forEach((entry) => {
      const key = String(entry.workDate || '').slice(0, 7);
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    });

    let monthKeys = Object.keys(grouped).sort();
    if (selectedExportMonths.length > 0) {
      monthKeys = monthKeys.filter((key) => selectedExportMonths.includes(key));
    }

    if (monthKeys.length === 0) {
      setError('No entries found for the selected month(s).');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const margin = 12;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const columns = [
      { key: 'employeeName', label: 'Employee', width: 45 },
      { key: 'workDate', label: 'Date', width: 24 },
      { key: 'startTime', label: 'Start', width: 18 },
      { key: 'endTime', label: 'End', width: 18 },
      { key: 'hoursWorked', label: 'Hours', width: 18 },
      { key: 'hourlyRateEur', label: '€/h', width: 18 },
      { key: 'earningsEur', label: 'Earned', width: 22 },
      { key: 'notes', label: 'Notes', width: 0 }
    ];
    const fixedWidth = columns.slice(0, -1).reduce((sum, col) => sum + col.width, 0);
    columns[columns.length - 1].width = (pageWidth - (margin * 2)) - fixedWidth;

    monthKeys.forEach((monthKey, index) => {
      if (index > 0) doc.addPage();

      const monthEntries = grouped[monthKey]
        .slice()
        .sort((a, b) => (a.workDate === b.workDate
          ? String(a.employeeName || '').localeCompare(String(b.employeeName || ''))
          : String(a.workDate).localeCompare(String(b.workDate))));

      let y = margin;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`Time Tracking Report - ${formatMonthLabel(monthKey)}`, margin, y);
      y += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let x = margin;
      columns.forEach((col) => {
        doc.text(col.label, x, y);
        x += col.width;
      });
      y += 2;
      doc.setLineWidth(0.2);
      doc.line(margin, y, pageWidth - margin, y);
      y += 4;

      doc.setFont('helvetica', 'normal');
      let monthMinutes = 0;
      let monthEarnings = 0;

      monthEntries.forEach((entry) => {
        if (y > pageHeight - margin - 10) {
          doc.addPage();
          y = margin;
        }

        x = margin;
        const rowValues = [
          entry.employeeName || '-',
          formatEuropeanDate(entry.workDate),
          entry.startTime || '-',
          entry.endTime || '-',
          Number(entry.hoursWorked || 0).toFixed(2),
          entry.hourlyRateEur === null || entry.hourlyRateEur === undefined ? '-' : Number(entry.hourlyRateEur).toFixed(2),
          entry.earningsEur === null || entry.earningsEur === undefined ? '-' : Number(entry.earningsEur).toFixed(2),
          entry.notes || '-'
        ];
        columns.forEach((col, colIndex) => {
          const [line] = doc.splitTextToSize(String(rowValues[colIndex]), col.width - 2);
          doc.text(line || '', x, y);
          x += col.width;
        });
        y += 5;
        monthMinutes += Number(entry.minutesWorked || 0);
        monthEarnings += Number(entry.earningsEur || 0);
      });

      y += 4;
      doc.setFont('helvetica', 'bold');
      doc.text(`Total hours: ${(monthMinutes / 60).toFixed(2)}    Total earnings: ${monthEarnings.toFixed(2)} EUR`, margin, y);
    });

    const fileSuffix = monthKeys.length > 1 ? `${monthKeys[0]}_to_${monthKeys[monthKeys.length - 1]}` : monthKeys[0];
    doc.save(`time-tracking-report-${fileSuffix}.pdf`);
  };

  return (
    <div>
      <button
        onClick={() => navigate('/my-time')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0, marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}
      >
        <ArrowLeft size={16} /> Back to My Time
      </button>

      <div className="tto-header">
        <div>
          <h1 style={{ color: '#1e293b', margin: 0 }}>Time Tracking Overview</h1>
          <p style={{ color: '#64748b', marginTop: '8px' }}>See all employee time entries and totals.</p>
        </div>
        <button
          onClick={loadOverview}
          className="tto-refresh-btn"
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {error && <div style={{ marginBottom: '14px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 12px' }}>{error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px', marginBottom: '14px' }}>
        <div className="tto-filters">
          <input
            type="text"
            placeholder="Employee name"
            value={filters.employee}
            onChange={(event) => setFilters((prev) => ({ ...prev, employee: event.target.value }))}
            style={inputStyle}
          />
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
            style={inputStyle}
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
            style={inputStyle}
          />
          <button
            onClick={() => setFilters({ employee: '', dateFrom: '', dateTo: '' })}
            style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }}
          >
            Clear filters
          </button>
        </div>

        <div className="tto-export-bar">
          <div className="tto-export-months">
            <span style={{ ...labelStyle, marginBottom: 0 }}>Export months:</span>
            {availableMonths.length === 0 ? (
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>No entries loaded.</span>
            ) : (
              availableMonths.map((monthKey) => (
                <label key={monthKey} className="tto-month-chip">
                  <input
                    type="checkbox"
                    checked={selectedExportMonths.includes(monthKey)}
                    onChange={() => toggleExportMonth(monthKey)}
                  />
                  {formatMonthLabel(monthKey)}
                </label>
              ))
            )}
          </div>
          <button
            onClick={handleExportPdf}
            disabled={entries.length === 0}
            className="tto-export-btn"
            style={{ cursor: entries.length === 0 ? 'not-allowed' : 'pointer' }}
          >
            <FileDown size={16} /> Export PDF{selectedExportMonths.length > 0 ? ` (${selectedExportMonths.length})` : ' (all)'}
          </button>
        </div>

        <div style={{ fontWeight: 700, color: '#1e293b', margin: '14px 0 8px' }}>Entries ({entries.length})</div>
        {loading ? (
          <div style={{ color: '#64748b' }}>Loading entries...</div>
        ) : entries.length === 0 ? (
          <div style={{ color: '#64748b' }}>No entries found.</div>
        ) : (
          <div className="tto-table-wrapper">
            <table className="tto-table">
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Start</th>
                  <th style={thStyle}>End</th>
                  <th style={thStyle}>Hours</th>
                  <th style={thStyle}>€/h</th>
                  <th style={thStyle}>Earned</th>
                  <th style={thStyle}>Notes</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isEditing = editingEntryId === entry.id;
                  return (
                    <tr key={entry.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>{entry.employeeName || '-'}</td>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editForm.workDate}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, workDate: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : formatEuropeanDate(entry.workDate)}
                      </td>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="time"
                            value={editForm.startTime}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, startTime: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : entry.startTime}
                      </td>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="time"
                            value={editForm.endTime}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, endTime: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : entry.endTime}
                      </td>
                      <td style={tdStyle}>{Number(entry.hoursWorked || 0).toFixed(2)}</td>
                      <td style={tdStyle}>{entry.hourlyRateEur === null || entry.hourlyRateEur === undefined ? '-' : Number(entry.hourlyRateEur).toFixed(2)}</td>
                      <td style={tdStyle}>{entry.earningsEur === null || entry.earningsEur === undefined ? '-' : Number(entry.earningsEur).toFixed(2)}</td>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.notes}
                            onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : (entry.notes || '-')}
                      </td>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => saveEditEntry(entry.id)}
                              disabled={savingEdit}
                              title="Save"
                              style={iconButtonStyle('#0f766e')}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={cancelEditEntry}
                              disabled={savingEdit}
                              title="Cancel"
                              style={iconButtonStyle('#64748b')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditEntry(entry)}
                            title="Edit entry"
                            style={iconButtonStyle('#334155')}
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form onSubmit={handleSaveRate} className="tto-rate-form">
        <div>
          <label style={labelStyle}>Employee</label>
          <select
            value={selectedRateUserId}
            onChange={(event) => setSelectedRateUserId(event.target.value)}
            style={inputStyle}
            required
          >
            <option value="">Select employee</option>
            {employees.map((employee) => (
              <option key={employee.userId} value={String(employee.userId)}>{employee.employeeName || `User ${employee.userId}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Hourly rate (€/h)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={rateForm.hourlyRateEur}
            onChange={(event) => setRateForm((prev) => ({ ...prev, hourlyRateEur: event.target.value }))}
            style={inputStyle}
            placeholder="e.g. 12.50"
            required
          />
        </div>
        <div>
          <label style={labelStyle}>Effective from</label>
          <input
            type="date"
            value={rateForm.effectiveFrom}
            onChange={(event) => setRateForm((prev) => ({ ...prev, effectiveFrom: event.target.value }))}
            style={inputStyle}
            required
          />
        </div>
        <button type="submit" disabled={savingRate || !selectedRateUserId} style={{ ...inputStyle, background: '#0f766e', color: 'white', cursor: savingRate || !selectedRateUserId ? 'not-allowed' : 'pointer', borderColor: '#0f766e', fontWeight: '700' }}>
          {savingRate ? 'Saving...' : 'Set €/h'}
        </button>
      </form>

      {rateMessage && <div style={{ marginBottom: '14px', background: '#ecfeff', color: '#155e75', border: '1px solid #a5f3fc', borderRadius: '10px', padding: '10px 12px' }}>{rateMessage}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px' }}>
        <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>Totals</div>
        <div style={{ color: '#334155', marginBottom: '10px' }}>Overall hours: <strong>{Number(totals.totalHours || 0).toFixed(2)}</strong></div>
        <div style={{ color: '#334155', marginBottom: '10px' }}>Overall earnings: <strong>{Number(totals.totalEarningsEur || 0).toFixed(2)} EUR</strong></div>
        <div style={{ color: '#334155', marginBottom: '10px' }}>
          Active €/h (selected user): <strong>{activeRatesByUser[selectedRateUserId] ? Number(activeRatesByUser[selectedRateUserId].hourlyRateEur).toFixed(2) : 'Not set'}</strong>
          {activeRatesByUser[selectedRateUserId] ? ` (from ${formatEuropeanDate(activeRatesByUser[selectedRateUserId].effectiveFrom)})` : ''}
        </div>
        <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>By employee:</div>
        {employeeSummary.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: '13px' }}>No entries in selection.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px', color: '#334155' }}>
            {employeeSummary.map((row) => (
              <li key={row.employeeName} style={{ marginBottom: '6px' }}>{row.employeeName}: {Number(row.totalHours || 0).toFixed(2)} h | {Number(row.totalEarningsEur || 0).toFixed(2)} EUR</li>
            ))}
          </ul>
        )}

        <div style={{ marginTop: '14px', color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>€/h history:</div>
        {ratesHistory.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: '13px' }}>No rates set yet.</div>
        ) : (
          <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>€/h</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ratesHistory.map((rate) => {
                  const isEditingRate = editingRateId === rate.id;
                  return (
                    <tr key={rate.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>
                        {isEditingRate ? (
                          <input
                            type="date"
                            value={rateEditForm.effectiveFrom}
                            onChange={(event) => setRateEditForm((prev) => ({ ...prev, effectiveFrom: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : formatEuropeanDate(rate.effectiveFrom)}
                      </td>
                      <td style={tdStyle}>
                        {isEditingRate ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={rateEditForm.hourlyRateEur}
                            onChange={(event) => setRateEditForm((prev) => ({ ...prev, hourlyRateEur: event.target.value }))}
                            style={editInputStyle}
                          />
                        ) : Number(rate.hourlyRateEur || 0).toFixed(2)}
                      </td>
                      <td style={tdStyle}>{rate.createdBy || 'System'}</td>
                      <td style={tdStyle}>
                        {isEditingRate ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => saveEditRate(rate.id)}
                              disabled={savingRateEdit}
                              title="Save"
                              style={iconButtonStyle('#0f766e')}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={cancelEditRate}
                              disabled={savingRateEdit}
                              title="Cancel"
                              style={iconButtonStyle('#64748b')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditRate(rate)}
                            title="Edit rate"
                            style={iconButtonStyle('#334155')}
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid #cbd5e1',
  boxSizing: 'border-box',
  fontSize: '14px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 700
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 8px',
  color: '#64748b',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const tdStyle = {
  padding: '10px 8px',
  color: '#334155'
};

const editInputStyle = {
  width: '100%',
  minWidth: '90px',
  padding: '4px 6px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  boxSizing: 'border-box',
  fontSize: '12px'
};

const iconButtonStyle = (color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  borderRadius: '6px',
  border: `1px solid ${color}`,
  background: '#fff',
  color,
  cursor: 'pointer'
});

export default TimeTrackingOverviewPage;
